/**
 * RailTooltip.tsx — the collapsed rail's tooltip (Sidebar v2 slice S3a, RULED
 * 2026-10-11; the spec at the operator's /root/klaxiora-sidebar-spec.md §2,
 * never copied here). ONE shared tooltip for every rail icon:
 *   · PORTALLED to <body> and position:fixed — the rail's nav scrolls on y,
 *     which clips x too, so a tooltip inside it would be cut off; the portal
 *     inherits the theme from the root (theme/theme.ts' portal rule).
 *   · Placed RAIL_TOOLTIP_GAP (10px) right of the icon, vertically centred
 *     (the CSS translates it up by half its own height).
 *   · Shown on hover, and on focus ONLY when the last input was a key
 *     (ruling 4): a mouse click that moves focus programmatically — Collapse
 *     handing focus to Expand — shows nothing.
 *   · Hoverable (ruling 11, WCAG 1.4.13): leaving the icon starts a grace
 *     delay; reaching the tooltip inside it keeps it open, leaving the
 *     tooltip starts the delay again. Dismissible by Escape.
 *   · Hidden on a pointer press, a scroll, blur, and never shown while a
 *     shell menu is open (the menus open where the tooltip would sit).
 *   · aria-hidden: each icon's accessible NAME is already its label; the
 *     count reaches assistive tech through aria-describedby (ruling 5).
 * The text is the trigger's data-tooltip attribute (DashboardLayout sets it
 * only in the rail); the caller's toggle calls hide() in its own handler.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'

export const RAIL_TOOLTIP_GAP = 10
export const RAIL_TOOLTIP_GRACE_MS = 150
const TRIGGER = '[data-tooltip]'
const OPEN_MENU = '[role="menu"]'

interface Tip { text: string; left: number; top: number }

export function useRailTooltip(rootRef: RefObject<HTMLElement | null>, enabled: boolean) {
  const [tip, setTip] = useState<Tip | null>(null)
  const keyboard = useRef(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const enabledRef = useRef(enabled)
  useEffect(() => { enabledRef.current = enabled }, [enabled])

  const cancelHide = useCallback(() => {
    if (hideTimer.current !== null) { clearTimeout(hideTimer.current); hideTimer.current = null }
  }, [])
  const hide = useCallback(() => { cancelHide(); setTip(null) }, [cancelHide])
  const hideSoon = useCallback(() => {
    cancelHide()
    hideTimer.current = setTimeout(() => { hideTimer.current = null; setTip(null) }, RAIL_TOOLTIP_GRACE_MS)
  }, [cancelHide])

  // Input modality, Escape, pointer presses and scrolls: document-wide.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { keyboard.current = true; if (e.key === 'Escape') hide() }
    const onPress = () => { keyboard.current = false; hide() }
    const onScroll = () => hide()
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('pointerdown', onPress, true)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('pointerdown', onPress, true)
      window.removeEventListener('scroll', onScroll, true)
      cancelHide()
    }
  }, [hide, cancelHide])

  // The triggers, by delegation on the rail's root.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const trigger = (t: EventTarget | null) => {
      const el = t instanceof Element ? t.closest<HTMLElement>(TRIGGER) : null
      return el && root.contains(el) ? el : null
    }
    const show = (el: HTMLElement) => {
      if (!enabledRef.current || root.querySelector(OPEN_MENU)) return
      const text = el.dataset.tooltip
      if (!text) return
      const r = el.getBoundingClientRect()
      cancelHide()
      setTip({ text, left: r.right + RAIL_TOOLTIP_GAP, top: r.top + r.height / 2 })
    }
    const onOver = (e: Event) => { const el = trigger(e.target); if (el) show(el) }
    const onOut = (e: Event) => { if (trigger(e.target)) hideSoon() }
    const onFocusIn = (e: Event) => { const el = trigger(e.target); if (el && keyboard.current) show(el) }
    const onFocusOut = () => hide()
    root.addEventListener('pointerover', onOver)
    root.addEventListener('pointerout', onOut)
    root.addEventListener('focusin', onFocusIn)
    root.addEventListener('focusout', onFocusOut)
    return () => {
      root.removeEventListener('pointerover', onOver)
      root.removeEventListener('pointerout', onOut)
      root.removeEventListener('focusin', onFocusIn)
      root.removeEventListener('focusout', onFocusOut)
    }
  }, [rootRef, hide, hideSoon, cancelHide])

  // The tooltip itself takes the pointer: reaching it keeps it open.
  const tipRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    node.addEventListener('pointerenter', cancelHide)
    node.addEventListener('pointerleave', hideSoon)
  }, [cancelHide, hideSoon])

  const portal = enabled && tip
    ? createPortal(
      <div ref={tipRef} data-rail-tooltip="" aria-hidden="true" className="k-tooltip" style={{ left: `${tip.left}px`, top: `${tip.top}px` }}>
        {tip.text}
      </div>,
      document.body,
    )
    : null
  return { portal, hide }
}
